import { forwardRef } from "react";
import { Button, cn } from "ui";
import { ArrowRightIcon } from "icons/outline";
import Link from "next/link";
import { useRouter } from "next/router";

type SectionProps = {
  id?: string;
  className?: string;
  children: React.ReactNode;
};

const SectionRoot = forwardRef<HTMLDivElement, SectionProps>(function Section(
  { id, className, children },
  ref
) {
  return (
    <section
      ref={ref}
      id={id}
      className={cn(
        "flex scroll-mt-[140px] flex-col gap-6 lg:scroll-mt-[100px]",
        className
      )}
    >
      {children}
    </section>
  );
});

const SectionActionButton = ({
  render,
  children,
}: {
  render: React.ReactElement;
  children: React.ReactNode;
}) => (
  <Button variant="tertiary" size="xs" render={render}>
    {children}
  </Button>
);

const SectionTitle = ({
  children,
  viewMoreLink,
  learnMoreLink,
}: {
  children: React.ReactNode;
  viewMoreLink?: string;
  learnMoreLink?: string;
}) => {
  const { query } = useRouter();

  return (
    <div className="flex items-center justify-between gap-1">
      <h2 className="text-lg font-medium text-foundations-boldest">
        {children}
      </h2>
      <div className="flex items-center gap-2">
        {learnMoreLink && (
          <SectionActionButton
            render={
              <Link
                href={learnMoreLink}
                target="_blank"
                rel="noopener noreferrer"
              />
            }
          >
            Learn more <ArrowRightIcon />
          </SectionActionButton>
        )}
        {viewMoreLink && (
          <SectionActionButton
            render={<Link href={{ pathname: viewMoreLink, query }} />}
          >
            View more <ArrowRightIcon />
          </SectionActionButton>
        )}
      </div>
    </div>
  );
};

export const Section = Object.assign(SectionRoot, {
  Title: SectionTitle,
});
